/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

import * as React from "react";
import { useState } from "react";
import { connect } from "react-redux";
import { Formik, Form, Field } from "formik";
import * as moment from "moment";

// @material-ui
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import { Grid, Divider } from "@material-ui/core";

// @material-ui/icons
import Face from "@material-ui/icons/Face";
import Person from "@material-ui/icons/Person";

// CT Kit
import Button from "components/CustomButtons/Button.jsx";
import Parallax from "components/Parallax/Parallax.jsx";
import InfoArea from "components/InfoArea/InfoArea.jsx";
import Media from "components/Media/Media.jsx";

import requireCheck from "client/hocs/requireCheck";
import { ChgPWDialog } from "client/views/Account/ChgPWDialog.tsx";
import { updateUser, ApricotAction } from "client/actions/user";
import {
  jobValidation,
  nameValidation,
  FormTextField,
  btnFormSubmit,
  btnFormAction,
} from "components/Form/FormField";
// import { Modal } from "components/Modal/Modal";
// import { deleteAWSUser } from "./authCognito.jsx";
import { renderJobSection, renderAreaSection } from "./accountUtils";
import { sSubmitChange, sChangeName } from "assets/jss/views/ResStrings";

import accountPageStyle from "assets/jss/views/accountPage/accountPageStyle.jsx";
import {
  idJobSubmit,
  idAreaSubmit,
  idNameSubmit,
  fnNameForm,
  idChgPW,
} from "assets/jss/views/ElementIDs";
const styles = createStyles(accountPageStyle as any);

interface Props extends WithStyles<typeof styles> {
  updateUser: ApricotAction;
  user: any;
}

const ProfilePageBase: React.FC<Props> = ({
  user,
  classes,
  // actions
  updateUser,
}: Props) => {
  const [changeName, setChangeName] = useState(false);
  const [showChgPW, setShowChgPW] = useState(false);
  // const [showAlert, setShowAlert] = useState(false);
  // const [alertModal, setAlertModal] = useState<React.ReactElement | null>(null);

  // const showMessage = (
  //   title: string,
  //   body: string,
  //   warning: boolean = false,
  // ) => {
  //   setAlertModal(
  //     <Modal title={title} warning={warning} onExit={() => setShowAlert(false)}>
  //       {body}
  //     </Modal>,
  //   );
  //   setShowAlert(true);
  // };

  // const deleteAccount = async (email: string, password: string) => {
  //   var res = await deleteAWSUser(email, password);
  //   if (res.result) {
  //     deletePhoenixAccount(); // TBD
  //   } else {
  //     showMessage(ttlDelAccountFail, res.err.message, true);
  //   }
  // };

  // const deletePhoenixAccount = () => {
  //   deleteUser(user._id);
  // };

  // after submitting a change, reset the behavior of Submit button
  const resetSubmit = (actions: any, item: string) => {
    actions.setSubmitting(false);
    actions.setTouched({ [item]: false });
    actions.validateForm(false);
  };

  const editName = (formikProps: any) => (
    <>
      {changeName ? (
        <Field
          name="name"
          type="text"
          icon={<Face />}
          component={FormTextField}
          label="Name"
          hidden={!changeName}
        />
      ) : (
        <h5 style={{ fontWeight: 400, margin: "0 1.8rem 0" }}>{user.name}</h5>
      )}
      <Button
        type="submit"
        size="sm"
        style={{ marginLeft: "1rem" }}
        color="success"
        disabled={
          changeName ? !formikProps.isValid || formikProps.isSubmitting : false
        }
        round>
        <span id={idNameSubmit}>
          {changeName ? sSubmitChange : sChangeName}
        </span>
      </Button>
      {btnFormAction({
        label: "Change Password",
        handler: () => setShowChgPW(true),
        size: "sm",
        id: idChgPW,
      })}
    </>
  );

  const renderLocalAccount = () => (
    <div style={{ maxWidth: "325px", margin: "0 auto -20px auto" }}>
      <Formik
        initialValues={{
          name: user.name,
        }}
        onSubmit={(values, actions) => {
          if (changeName === false) {
            setChangeName(true);

            actions.setSubmitting(false);
          } else {
            updateUser(user._id, values);
            resetSubmit(actions, "name");
          }
        }}
        validationSchema={nameValidation}
        enableReinitialize={true}
        render={(formikProps) => (
          <Form name={fnNameForm}>{editName(formikProps)}</Form>
        )}
      />
    </div>
  );

  const renderProfileJobs = () => (
    <div className={classes.profileGrid}>
      <Formik
        initialValues={{
          jobs: user.jobs,
        }}
        onSubmit={(values, actions) => {
          updateUser(user._id, values);
          resetSubmit(actions, "jobs");
        }}
        validationSchema={jobValidation}
        enableReinitialize={true}
        render={(formikProps) => (
          <Form>
            {renderJobSection(formikProps, classes)}
            {btnFormSubmit({
              label: sSubmitChange,
              disabled: !formikProps.isValid || formikProps.isSubmitting,
              size: "sm",
              id: idJobSubmit,
            })}
          </Form>
        )}
      />
    </div>
  );

  const renderProfileAreas = () => (
    <div className={classes.profileGrid}>
      <Formik
        initialValues={{
          areas: user.areas || [],
        }}
        onSubmit={(values, actions) => {
          updateUser(user._id, values);
          resetSubmit(actions, "areas");
        }}
        // validationSchema={areaValidation}  // No validation is required, it's fine without answer
        enableReinitialize={true}
        render={(formikProps) => (
          <Form>
            {renderAreaSection(formikProps, classes)}
            {btnFormSubmit({
              label: sSubmitChange,
              disabled: !formikProps.isValid || formikProps.isSubmitting,
              size: "sm",
              id: idAreaSubmit,
            })}
          </Form>
        )}
      />
    </div>
  );

  const socialSource = (user: any) => {
    const btn = (link: string, source: string) => (
      <Button
        key={source}
        justIcon
        round
        color={source}
        onClick={() => window.open(link, "_blank")}
        style={{ marginLeft: "0.5rem" }}>
        <i
          className={`fab fa-${source === "facebook" ? "facebook-f" : source}`}
        />
      </Button>
    );

    const ret: React.ReactElement[] = [];

    if (user.googleId) {
      ret.push(btn("https://myaccount.google.com/", "google"));
    }
    if (user.facebookId) {
      ret.push(btn("https://www.facebook.com/", "facebook"));
    }
    if (user.twitterId) {
      ret.push(btn("https://twitter.com/", "twitter"));
    }
    if (user.azureId) {
      ret.push(btn("", "Azure"));
    }
    return ret;
  };

  const renderSocialAccount = () => (
    <div style={{ maxWidth: "360px", margin: "0 auto", display: "flex" }}>
      <Media
        avatar={
          user.picture ||
          "https://apricad-console.herokuapp.com/LogoFavIcon.png"
        }
        // title={<p style={{ fontWeight: 400, margin: 0 }}>{user.name}</p>}
        title={user.name}
        body={
          <div style={{ marginTop: "-16px" }}>
            Signed up via&nbsp;{socialSource(user)}
          </div>
        }
      />
    </div>
  );

  const renderName = () => (
    <>
      <InfoArea
        className={classes.infoArea}
        title="Account"
        description={`Member since ${moment(user.dateSignup).format("LL")}`}
        icon={Person}
        iconColor="info"
      />
      {user.localId ? renderLocalAccount() : renderSocialAccount()}
    </>
  );

  // const handleDelete = (id: number, input: string) => {
  //   const isLocalId: boolean = user.localId ? true : false;
  //   const { email } = user;

  //   if (isLocalId) {
  //     deleteAccount(email, input);
  //   } else {
  //     if (input.toUpperCase() === email.toUpperCase()) {
  //       deletePhoenixAccount();
  //     } else {
  //       showMessage(ttlDelAccountFail, msgDelAccountFail, true);
  //     }
  //   }
  // };

  // const renderDelButton = () => {
  //   const isLocalId: boolean = user.localId ? true : false;
  //   var sprintf = require("sprintf-js").sprintf;

  //   const body = sprintf(
  //     msgDeleteAccount,
  //     isLocalId ? "password" : "email address",
  //   );

  //   const deleteClickHandler = () => {
  //     setAlertModal(
  //       <Modal
  //         title={ttlDeleteAccount}
  //         color={warning ? "warning" : "secondary"}
  //         onButtons={handleDelete}
  //         password={isLocalId}
  //         onExit={() => setShowAlert(false)}>
  //         {body}
  //       </Modal>,
  //     );
  //     setShowAlert(true);
  //   };

  //   return (
  //     <Button
  //       onClick={deleteClickHandler}
  //       style={{ display: "block", maxWidth: "360px", margin: "1rem auto" }}
  //       color="warning"
  //       size="sm"
  //       round>
  //       Delete Account
  //     </Button>
  //   );
  // };

  return (
    <>
      <Parallax
        image={require("assets/img/profile.jpg")}
        filter="course"
        banner>
        <div className={classes.container}>
          <div className={classes.brand}>
            <h1 className={classes.title}>My Profile</h1>
          </div>
        </div>
      </Parallax>

      {/* {showAlert && alertModal} */}
      {showChgPW && (
        <ChgPWDialog email={user.email} onExit={() => setShowChgPW(false)} />
      )}

      <div className={classes.pageContent} style={{ padding: "30px 0" }}>
        <div className={classes.container}>
          <Grid container justify="center">
            <Grid item xs={12} sm={6} md={6}>
              {renderName()}
              {renderProfileJobs()}
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              {renderProfileAreas()}
            </Grid>
          </Grid>
          <Divider style={{ marginTop: "1rem" }} variant="fullWidth" />
          {/* <div className={classes.textCenter}>{renderDelButton()}</div> */}
        </div>
      </div>
    </>
  );
};

function mapStateToProps(state: any) {
  return {
    user: state.state_auth,
  };
}

const base: any = ProfilePageBase;
export const ProfilePage = {
  component: requireCheck(
    withStyles(styles)(connect(mapStateToProps, { updateUser })(base)),
  ),
};

export const ProfilePageTest = withStyles(styles)(base);
